<template>
  <ComponentHeader title="Question Library" :subtitle="`${filteredQuestions.length} questions`" :heading-level="3">
    <button
      @click="openCreateModal"
      class="btn-primary"
    >
      <font-awesome-icon :icon="['fas', 'plus']" />
      New Question
    </button>
  </ComponentHeader>

  <div class="question-library p-4">
    <!-- Search & Filters -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div class="flex gap-4">
        <div class="flex-1 relative">
          <font-awesome-icon :icon="['fas', 'search']" class="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by intent or question text..."
            class="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all"
          />
        </div>
        <select
          v-model="filterInstrument"
          class="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all"
        >
          <option value="">All Instruments</option>
          <option v-for="inst in instruments" :key="inst.id" :value="inst.id">
            {{ inst.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-20">
      <font-awesome-icon :icon="['fas', 'spinner']" class="text-primary-600 text-4xl animate-spin" />
    </div>

    <!-- Questions List -->
    <div v-else class="space-y-3">
      <div
        v-for="question in filteredQuestions"
        :key="question.id"
        class="group bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-lg hover:border-primary-200 transition-all duration-200"
      >
        <div class="p-6">
          <div class="flex items-start justify-between">
            <div class="flex-1 mr-4">
              <div class="flex items-center gap-3 mb-2">
                <h4 class="text-lg font-semibold text-gray-900">{{ question.intent }}</h4>
                <span
                  v-if="question.conditional_followup?.has_conditional"
                  class="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full"
                >
                  <font-awesome-icon :icon="['fas', 'code-branch']" class="mr-1" />
                  Conditional
                </span>
              </div>
              <p class="text-gray-600 text-sm leading-relaxed mb-3">{{ question.sample_question_text }}</p>

              <div class="flex items-center gap-4 text-xs text-gray-500 mb-3">
                <span>
                  <font-awesome-icon :icon="['fas', 'clipboard-list']" class="mr-1" />
                  {{ question.instrument_count }} instrument(s)
                </span>
                <span v-if="question.entity_types.length">
                  <font-awesome-icon :icon="['fas', 'tags']" class="mr-1" />
                  {{ question.entity_types.join(', ') }}
                </span>
              </div>

              <!-- Instrument Badges with Dynamic Colors -->
              <div v-if="question.instruments && question.instruments.length > 0" class="flex flex-wrap gap-2">
                <span
                  v-for="instrument in question.instruments"
                  :key="instrument.id"
                  :style="getInstrumentBadgeStyle(instrument.name)"
                  class="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border hover:opacity-80 transition-opacity"
                  :title="`Order: ${instrument.order}`"
                >
                  <font-awesome-icon :icon="['fas', 'clipboard-check']" />
                  {{ instrument.name }}
                </span>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <button
                @click="editQuestion(question)"
                class="p-2.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title="Edit"
              >
                <font-awesome-icon :icon="['fas', 'pen-to-square']" />
              </button>
              <button
                @click="deleteQuestion(question)"
                class="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete"
              >
                <font-awesome-icon :icon="['fas', 'trash']" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredQuestions.length === 0" class="text-center py-16 bg-gray-50 rounded-lg">
        <font-awesome-icon :icon="['fas', 'circle-question']" class="text-gray-300 text-6xl mb-4" />
        <p class="text-gray-600 text-lg">No questions found</p>
      </div>
    </div>

    <!-- Question Editor Modal -->
    <QuestionEditor
      v-if="showEditor"
      :question="editingQuestion"
      @save="saveQuestion"
      @close="closeEditor"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import QuestionEditor from './QuestionEditor.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';

export default {
  name: 'QuestionLibrary',

  components: {
    FontAwesomeIcon,
    QuestionEditor,
    ComponentHeader
  },

  data() {
    return {
      questions: [],
      instruments: [],
      loading: true,
      searchQuery: '',
      filterInstrument: '',
      showEditor: false,
      editingQuestion: null
    };
  },

  computed: {
    filteredQuestions() {
      let filtered = this.questions;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(q =>
          q.intent.toLowerCase().includes(query) ||
          q.sample_question_text.toLowerCase().includes(query)
        );
      }

      if (this.filterInstrument) {
        // Filter by instrument using the new instruments array
        filtered = filtered.filter(q =>
          q.instruments?.some(inst => inst.id === parseInt(this.filterInstrument))
        );
      }

      return filtered;
    }
  },

  mounted() {
    this.fetchQuestions();
    this.fetchInstruments();
  },

  methods: {
    /**
     * Generate a consistent color from a string
     * @param {string} str - The string to convert to a color
     * @returns {object} Object with backgroundColor, textColor, and borderColor
     */
    stringToColor(str) {
      // Generate hash from string
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
        hash = hash & hash; // Convert to 32-bit integer
      }

      // Convert hash to hue (0-360)
      const hue = Math.abs(hash % 360);

      // Use HSL for better control over saturation and lightness
      // Saturation: 65-75% for vibrant but not oversaturated colors
      // Lightness: 85-92% for light backgrounds that work with dark text
      const saturation = 65 + (Math.abs(hash) % 10);
      const lightness = 85 + (Math.abs(hash >> 8) % 7);

      // Generate background color (light)
      const bgColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

      // Generate text color (darker version of the same hue)
      const textLightness = 25 + (Math.abs(hash >> 16) % 15);
      const textColor = `hsl(${hue}, ${saturation}%, ${textLightness}%)`;

      // Generate border color (medium version)
      const borderLightness = 60 + (Math.abs(hash >> 24) % 10);
      const borderColor = `hsl(${hue}, ${saturation}%, ${borderLightness}%)`;

      return {
        backgroundColor: bgColor,
        textColor: textColor,
        borderColor: borderColor
      };
    },

    /**
     * Get badge style object for an instrument
     * @param {string} instrumentName - The name of the instrument
     * @returns {object} Style object for the badge
     */
    getInstrumentBadgeStyle(instrumentName) {
      const colors = this.stringToColor(instrumentName);
      return {
        backgroundColor: colors.backgroundColor,
        color: colors.textColor,
        borderColor: colors.borderColor
      };
    },

    async fetchQuestions() {
      this.loading = true;
      try {
        const response = await this.$axios.get('/api/questions/');
        this.questions = response.data;
      } catch (error) {
        console.error('Error fetching questions:', error);
        this.$emit('show-message', 'Failed to load questions', 'error');
      } finally {
        this.loading = false;
      }
    },

    async fetchInstruments() {
      try {
        const response = await this.$axios.get('/api/survey-instruments/');
        this.instruments = response.data;
      } catch (error) {
        console.error('Error fetching instruments:', error);
      }
    },

    openCreateModal() {
      this.editingQuestion = null;
      this.showEditor = true;
    },

    editQuestion(question) {
      this.editingQuestion = { ...question };
      this.showEditor = true;
    },

    async saveQuestion(questionData) {
      try {
        if (questionData.id) {
          await this.$axios.patch(`/api/questions/${questionData.id}/`, questionData);
          this.$emit('show-message', 'Question updated successfully', 'success');
        } else {
          await this.$axios.post('/api/questions/', questionData);
          this.$emit('show-message', 'Question created successfully', 'success');
        }
        this.closeEditor();
        this.fetchQuestions();
        this.$emit('refresh');
      } catch (error) {
        console.error('Error saving question:', error);
        this.$emit('show-message', 'Failed to save question', 'error');
      }
    },

    async deleteQuestion(question) {
      if (!confirm(`Delete question: "${question.intent}"?`)) return;

      try {
        await this.$axios.delete(`/api/questions/${question.id}/`);
        this.$emit('show-message', 'Question deleted successfully', 'success');
        this.fetchQuestions();
        this.$emit('refresh');
      } catch (error) {
        console.error('Error deleting question:', error);
        this.$emit('show-message', 'Failed to delete question', 'error');
      }
    },

    closeEditor() {
      this.showEditor = false;
      this.editingQuestion = null;
    }
  }
};
</script>

<style scoped>
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
